/**
 * Toast region + useToast hook, top-right toasts, auto-dismiss after 4s,
 * color-coded by status. Per UX-DESIGN.md § Cross-cutting visual language.
 *
 * Usage:
 *   const { showToast } = useToast();
 *   showToast({ kind: 'success', message: 'Saved' });
 *
 * Mount <ToastRegion /> once at the app root (App.tsx).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { IconCheck, IconClose } from './Icon.tsx';
import { reportError, shouldAutoNotifyAdmin } from '../lib/errorReport.ts';

export type ToastKind = 'success' | 'error' | 'info';

/**
 * Push 4-1ah (2026-07-10): optional action affordance embedded in the toast.
 * Used for error-recovery affordances like "Buy credits →" on insufficient-
 * credits toasts. Rendered as an inline button in the toast body; click
 * fires `onClick` (typically opens the BuyCreditsModal via useBuyCredits())
 * and the toast auto-dismisses so it doesn't linger behind the modal.
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

/**
 * Push 4-1ai (2026-07-10): default recovery-action mapping keyed by HTTP
 * status. Dispatched via CustomEvents so Toast.tsx doesn't need direct
 * access to the BuyCreditsProvider / session context / router — anything
 * that wants to handle a recovery action mounts a listener at the app
 * root (typically App.tsx or BuyCreditsProvider).
 *
 * Rationale: showApiErrorToast is called from ~50 places; centralizing
 * the "which action for which status" logic here means every callsite
 * benefits automatically. Overrides still work — callers pass an
 * explicit `action` in the options bag and we skip the default.
 *
 * Events emitted (all as no-arg CustomEvents unless noted):
 *   - 'olytic:buy-credits'    → BuyCreditsProvider listens, opens the modal
 *   - 'olytic:contact-admin'  → RequestModal listens (or falls back to a
 *                                 mailto: via the admin-contact endpoint)
 *   - 'olytic:retry-last'     → Not yet listened; callers supply their
 *                                 own retry via the action override
 */
function emitRecoveryEvent(name: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(name));
  } catch {
    // Defensive — never let dispatch failure break the toast path.
  }
}

/**
 * Push 4-1ai (2026-07-10): produce a default recovery action for a given
 * error status. Returns null when no recovery action is appropriate for
 * the status (or when the caller wants to override via options.action).
 *
 * Actions dispatch CustomEvents (see emitRecoveryEvent above) — Toast.tsx
 * stays context-free.
 */
function defaultRecoveryAction(status: number | undefined): ToastAction | null {
  if (typeof status !== 'number') return null;
  // 402 — out of credits. Handled by dedicated ChatApprovalBanner in chat
  // surfaces; for non-chat surfaces (buy from Settings, etc.) the buy-
  // credits modal is one click away.
  if (status === 402) {
    return {
      label: 'Buy credits',
      onClick: () => emitRecoveryEvent('olytic:buy-credits'),
    };
  }
  // 403 — permission wall. Auto-notify fires (see shouldAutoNotifyAdmin)
  // AND we give the user a "Contact admin" affordance as a manual escape.
  if (status === 403) {
    return {
      label: 'Contact admin',
      onClick: () => emitRecoveryEvent('olytic:contact-admin'),
    };
  }
  // 401 — session expired. SessionExpiryListener handles the redirect
  // automatically via the `olytic:session-expired` event, so we don't
  // duplicate here. No visible action button.
  // 404 — record missing. No universal recovery; callers can add a
  // context-specific action if useful.
  // 429 — rate limited. No action; the toast copy already tells the
  // user to wait a moment.
  // 5xx + 0 (network) — reportError fires; the toast message picks up
  // the "Your admin has been notified" suffix. No default action button
  // (callers can pass a Retry callback via options).
  return null;
}

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  /**
   * Optional raw technical detail (e.g. server-side stack info or the
   * unmodified Postgres error). Rendered below the friendly message in a
   * small monospace block with a Copy button so an admin or support engineer
   * can paste it into a ticket.
   */
  detail?: string | null;
  /**
   * Push #78 round 11 (item A foundation) — sys__operator_alert.id from
   * the server. Included in the Copy-for-support clipboard string so
   * support can grep production logs by id instead of by timestamp.
   */
  incidentId?: string | null;
  /** Push 4-1ah (2026-07-10): optional recovery action button. */
  action?: ToastAction | null;
  ttl: number;
}

interface ShowToastInput {
  kind: ToastKind;
  message: string;
  detail?: string | null;
  incidentId?: string | null;
  /** Push 4-1ah (2026-07-10): optional recovery action button. */
  action?: ToastAction | null;
  /** ms before auto-dismiss; default 4000 (errors with details stick longer) */
  ttl?: number;
}

interface ToastContextValue {
  showToast: (input: ShowToastInput) => void;
  dismissToast: (id: number) => void;
  toasts: Toast[];
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismissToast = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    ({ kind, message, detail, incidentId, action, ttl }: ShowToastInput) => {
      const id = nextId++;
      // Errors with a detail block stick around longer so the user can
      // actually read + copy before the toast dismisses itself.
      // Push 4-1ah (2026-07-10): toasts with a recovery action also stick
      // longer so the user has time to click the action button before it
      // auto-dismisses.
      const computedTtl =
        ttl !== undefined
          ? ttl
          : detail !== undefined && detail !== null
            ? 12000
            : action !== undefined && action !== null
              ? 10000
              : 4000;
      setToasts((list) => [
        ...list,
        {
          id,
          kind,
          message,
          detail: detail ?? null,
          incidentId: incidentId ?? null,
          action: action ?? null,
          ttl: computedTtl,
        },
      ]);
    },
    [],
  );

  return (
    <ToastContext.Provider value={{ showToast, dismissToast, toasts }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === undefined) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}

/**
 * Convenience for the common "catch an ApiError, surface as a toast" pattern.
 * Pulls friendly + detail from the ApiError shape so callers don't have to
 * remember to forward both fields.
 *
 *   catch (err) {
 *     showApiErrorToast(showToast, err, 'Could not load connectors.');
 *   }
 *
 * For non-ApiError throws (unexpected), uses the provided fallback message.
 */
export function showApiErrorToast(
  show: ToastContextValue['showToast'],
  err: unknown,
  fallback: string,
  options?: {
    /** Push 4-1ah (2026-07-10): optional recovery action. Callers pass in an
     *  action (typically "Buy credits →") for insufficient-credit paths so
     *  the toast surfaces a one-click escape. Overrides the Push 4-1ai
     *  auto-attached default from defaultRecoveryAction(). */
    action?: ToastAction | null;
    /** Optional predicate: only attach the action when it returns true.
     *  Common shape — only attach on status === 402. Keeps callers from
     *  needing to inspect err.status inline. */
    when?: (err: unknown) => boolean;
    /**
     * Push 4-1ai (2026-07-10): opt out of the auto-notify-admin flow.
     * Default is: 5xx / network / 403 errors fire reportError() in the
     * background + append "Your admin has been notified" to the toast.
     * Pass `skipAutoNotify: true` for expected error paths (e.g.
     * optimistic UI that expects 404, background polls) so the user's
     * admin doesn't get spammed on non-actionable errors.
     */
    skipAutoNotify?: boolean;
    /**
     * Push 4-1ai (2026-07-10): the UI surface for context-aware reporting.
     * Threaded through to reportError() so admins can see "Jane hit a
     * 500 in Connectors" instead of "Jane hit a 500 somewhere."
     */
    surface?: string;
  },
): void {
  // Avoid a hard dependency on ApiError to prevent import cycles; structural
  // check is enough because we control both ends.
  const e = err as {
    name?: string;
    status?: number;
    message?: string;
    detail?: string | null;
    incidentId?: string | null;
    code?: string | null;
  } | null;

  // Push 4-1ai (2026-07-10): auto-notify admins for high-severity errors.
  // Fire-and-forget — never throws, never awaits.
  const willAutoNotify =
    options?.skipAutoNotify !== true && shouldAutoNotifyAdmin(err);
  if (willAutoNotify) {
    void reportError(err, {
      ...(options?.surface !== undefined ? { surface: options.surface } : {}),
    });
  }

  // Explicit caller action wins; otherwise attach the default for the
  // status. Callers can still fully suppress by passing `action: null`.
  let action: ToastAction | null = null;
  if (options?.action !== undefined) {
    const shouldAttachOverride =
      options.action !== null &&
      (options.when === undefined || options.when(err));
    action = shouldAttachOverride ? options.action : null;
  } else {
    action = defaultRecoveryAction(e?.status);
  }

  const baseMessage =
    e !== null && e?.name === 'ApiError' && typeof e.message === 'string'
      ? e.message
      : fallback;
  // Append "Your admin has been notified" suffix so the user knows they're
  // not shouting into the void. Keeps the friendly polite-voice tone (not
  // error-voice per PATTERNS.md).
  const finalMessage = willAutoNotify
    ? `${baseMessage} Your admin has been notified.`
    : baseMessage;

  if (e !== null && e?.name === 'ApiError' && typeof e.message === 'string') {
    show({
      kind: 'error',
      message: finalMessage,
      detail: e.detail ?? null,
      incidentId: e.incidentId ?? null,
      action,
    });
    return;
  }
  show({ kind: 'error', message: finalMessage, action });
}

export function ToastRegion() {
  const { toasts, dismissToast } = useToast();
  return (
    <div className="toast-region" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismissToast(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  // Phase 1.20 (2026-06-29): graceful exit via two-stage dismiss.
  // When TTL elapses we flip `dismissing` to true (CSS animates the
  // toast-out keyframe — opacity 1→0 + slight slide), then after the
  // animation duration we actually unmount via onDismiss. The result
  // is a smooth fade for "thing installed" success toasts instead of
  // the prior abrupt removal. Click-to-close (the × button) also
  // routes through this two-stage path so the UX is consistent.
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (toast.ttl <= 0) return;
    const fadeAt = setTimeout(() => setDismissing(true), toast.ttl);
    return () => clearTimeout(fadeAt);
  }, [toast.ttl]);

  useEffect(() => {
    if (!dismissing) return;
    // Match the toast-out keyframe duration (280ms) before unmounting.
    const unmountAt = setTimeout(onDismiss, 280);
    return () => clearTimeout(unmountAt);
  }, [dismissing, onDismiss]);

  function handleClose(): void {
    setDismissing(true);
  }

  // No emojis \u2014 SVG icons via Icon set. Severity-tinted by parent span.
  const icon =
    toast.kind === 'success' ? (
      <IconCheck size={14} />
    ) : toast.kind === 'error' ? (
      <IconClose size={14} />
    ) : (
      <span style={{ fontWeight: 700, fontSize: '13px' }}>i</span>
    );

  async function copyForSupport() {
    // Push #78 round 11 (item A foundation) — clipboard string carries
    // the incident_id from sys__operator_alert so support can run
    // `SELECT * FROM workspace_directory.sys__operator_alert WHERE id =
    // '<uuid>'` and find the exact row + breadcrumbs in seconds.
    const lines: string[] = [];
    lines.push(toast.message);
    if (toast.incidentId !== null && toast.incidentId !== undefined) {
      lines.push(`Incident ID: ${toast.incidentId}`);
    }
    if (toast.detail !== null && toast.detail !== undefined && toast.detail !== '') {
      lines.push('');
      lines.push('Technical details:');
      lines.push(toast.detail);
    }
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore clipboard errors */
    }
  }

  // a11y: errors use role="alert" + aria-live="assertive" so assistive
  // tech interrupts on failure; success / info use role="status" + polite.
  const isError = toast.kind === 'error';
  return (
    <div
      className={`toast toast-${toast.kind}${dismissing ? ' toast-dismissing' : ''}`}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
    >
      <span
        className="toast-icon"
        style={{
          color:
            toast.kind === 'success'
              ? 'var(--olytic-green)'
              : toast.kind === 'error'
                ? 'var(--olytic-red)'
                : 'var(--olytic-teal)',
          fontWeight: 700,
          fontSize: '13px',
        }}
        aria-hidden="true"
      >
        {icon}
      </span>
      <div className="toast-body">
        <div className="toast-message">{toast.message}</div>
        {/* Push 4-1ah (2026-07-10): recovery action button. Fires the
           caller's onClick + auto-dismisses the toast so it doesn't linger
           behind any modal the action opens. */}
        {toast.action !== null && toast.action !== undefined && (
          <div style={{ marginTop: 6 }}>
            <button
              type="button"
              className="btn btn-link"
              style={{ padding: 0, fontSize: 12, fontWeight: 600 }}
              onClick={() => {
                toast.action?.onClick();
                setDismissing(true);
              }}
            >
              {toast.action.label} {'→'}
            </button>
          </div>
        )}
        {toast.detail !== null && toast.detail !== undefined && toast.detail !== '' && (
          <>
            {/* Push #78 acceptance-criteria sweep. hide the raw detail
               behind a <details> disclosure so users see clean copy by
               default and dev-speak (Postgres error text, stack-traceish
               bits) only appears when they explicitly drill in for
               support. Copy-for-support still grabs both lines. */}
            <details className="toast-detail-disclosure">
              <summary>Technical details</summary>
              <div className="toast-detail">{toast.detail}</div>
            </details>
            <button
              type="button"
              className="toast-copy"
              onClick={copyForSupport}
              aria-label="Copy error details for support"
            >
              {copied ? 'Copied' : 'Copy for support'}
            </button>
          </>
        )}
      </div>
      <button
        type="button"
        className="toast-close"
        onClick={handleClose}
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  );
}
